import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getSettings,
  saveSiteSettings,
} from "../../../reduxToolkit/features/adminPanel/webSettings";
import { getNotification } from "../../../reduxToolkit/features/adminPanel/notification";
import { CATEGORIES } from "../../../utils/cookieConsent";
import { colors, radii } from "../../../theme/designTokens";

const DEFAULT_BANNER_TEXT =
  "Elifesaving Medicine uses cookies to enhance your browsing experience and improve our services. Learn more in our Cookie Policy.";
const DEFAULT_INTRO_TEXT =
  "We use cookies to run this site, understand how it's used, and show third-party content like Trustpilot reviews. Choose which categories you allow. You can change this anytime from \"Cookie Settings\" in the footer.";

export default function CookieConsentSettings() {
  const dispatch = useDispatch();
  const localData = JSON.parse(localStorage.getItem("adminLoginInfo"));
  const { webSettings } = useSelector((state) => state.webSettings);

  const [enabled, setEnabled] = useState(true);
  const [bannerText, setBannerText] = useState(DEFAULT_BANNER_TEXT);
  const [dialogIntroText, setDialogIntroText] = useState(DEFAULT_INTRO_TEXT);
  const [categories, setCategories] = useState(
    CATEGORIES.map((c) => ({ key: c.key, label: c.label, description: c.description }))
  );

  useEffect(() => {
    const saved = (webSettings && webSettings.cookieConsent) || {};
    setEnabled(saved.enabled !== false);
    setBannerText(saved.bannerText || DEFAULT_BANNER_TEXT);
    setDialogIntroText(saved.dialogIntroText || DEFAULT_INTRO_TEXT);
    setCategories(
      CATEGORIES.map((c) => {
        const override =
          saved.categories && saved.categories.find((s) => s.key === c.key);
        return {
          key: c.key,
          label: (override && override.label) || c.label,
          description: (override && override.description) || c.description,
        };
      })
    );
  }, [webSettings]);

  const handleCategoryChange = (key, field, value) => {
    setCategories((prev) =>
      prev.map((c) => (c.key === key ? { ...c, [field]: value } : c))
    );
  };

  const handleSave = () => {
    dispatch(
      saveSiteSettings({
        cookieConsent: {
          enabled,
          bannerText,
          dialogIntroText,
          categories,
        },
        creatorId: localData._id,
      })
    );
    setTimeout(() => {
      dispatch(getNotification());
      dispatch(getSettings());
    }, 1000);
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        }
        label="Show Cookie Consent Banner"
        sx={{ mb: 1 }}
      />

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.ink3, mt: 2, mb: 1 }}>
        Banner Message
      </Typography>
      <TextField
        value={bannerText}
        onChange={(e) => setBannerText(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
        helperText="Shown in the small floating banner that first appears to visitors."
      />

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.ink3, mt: 2, mb: 1 }}>
        Cookie Preferences Dialog Intro
      </Typography>
      <TextField
        value={dialogIntroText}
        onChange={(e) => setDialogIntroText(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
        helperText='Shown at the top of the "Cookie Preferences" dialog, above the category toggles.'
      />

      <Divider sx={{ my: 2.5 }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.ink3, mb: 1.5 }}>
        Cookie Categories
      </Typography>
      {categories.map((cat, i) => {
        const isRequired = CATEGORIES.find((c) => c.key === cat.key)?.required;
        return (
          <Box
            key={cat.key}
            sx={{
              border: `1px solid ${colors.line}`,
              borderRadius: radii.md,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip
                size="small"
                label={isRequired ? "Always On" : "Optional"}
                color={isRequired ? "default" : "primary"}
                variant="outlined"
              />
              <Typography sx={{ fontSize: 12, color: colors.ink3 }}>
                key: {cat.key}
              </Typography>
            </Box>
            <TextField
              label="Label"
              value={cat.label}
              onChange={(e) => handleCategoryChange(cat.key, "label", e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              label="Description"
              value={cat.description}
              onChange={(e) =>
                handleCategoryChange(cat.key, "description", e.target.value)
              }
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
          </Box>
        );
      })}

      <Button
        variant="contained"
        color="success"
        size="small"
        fullWidth
        onClick={handleSave}
      >
        Save
      </Button>
    </Box>
  );
}
